import React from "react";
import ObjectSelect from "./ObjectSelect";
import SelectionList from "./SelectionList";
import TemplateKind from "../models/TemplateKind";
import ArrayInput from "./ArrayInput";
import WeekList from "./WeekList";
import TextDisplay from "./TextDisplay";
import Mousetrap from "@timeedit/mousetrap";
import Language from "../lib/Language";
import API from "../lib/TimeEditAPI";
import _ from "underscore";
import Popover from "./Popover";
import ObjectInfo from "./ObjectInfo";
import GroupPaneWrapper from "./GroupPaneWrapper";
import Log from "../lib/Log";
import SelectionListFieldEditor from "./SelectionListFieldEditor";
import { TimeConstants as TC } from "../lib/TimeConstants";
import TrackPane from "./TrackPane";
import { McFluffy } from "../models/McFluffy";
import ObjectNameCache from "../lib/ObjectNameCache";
import { OPERATION_TYPES } from "./preferences/PrefsCoreAPI";
import { ClusterKind, EntryKind } from "../lib/EntryConstants";
import { Calendar } from "../models/Calendar";
import { ObjectHeader } from "../models/ObjectHeader";
import { Macros } from "../models/Macros";
import { MemberData } from "../models/MemberData";
import { Selection } from "../models/Selection";
import { TFluffyObjectItem } from "../types/models/TMcFluffy";
import { TSelectedCategories } from "../types/models/TSearchSettings";
import { IGetTemplateGroupsResponse } from "../types/api/APIResponseTypes";
import { TActivityToReservationMapObj, TObject } from "../types/api/shared";
import { TrackVariation } from "../lib/ActivityUtils";
import { Entry } from "../models/Entry";
import { User } from "../models/User";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";
import { findNextSelectedItem, isRequestObject } from "../lib/SelectionUtils";
import { ObjectSearch } from "../models/ObjectSearch";
import {
    getAvailableWidth,
    getListWidths,
    getResizeWeights,
    HELP_WIDTH,
} from "../lib/SelectionPaneUtils";
import { LDFlagSet } from "launchdarkly-react-client-sdk";

const NARROW_WIDTH = 1024;

/**
 * What GroupInformationPane hands back to this pane: a reservation row rather
 * than an Entry. Both handlers below read reservationids when it is there and
 * fall back to the row's own id.
 */
type TGroupRow = {
    id: number;
    reservationids?: number[];
    group?: number;
    groups?: number[];
};

type TSelectionPaneProps = {
    id: number;
    activeCalendar: Calendar;
    data: Selection;
    activeLayer: number;
    onEntryInfoOpen: (
        entry: Entry | Entry[] | number[],
        showPanel?: boolean,
        editMode?: boolean
    ) => void;
    unlockedReservations: number[];
    endLockMode: () => void;
    size: {
        width: number;
        height: number;
    };
    isVisible: boolean;
    hasOrderList: () => void;
    user: User;
    getObjectSearch: () => ObjectSearch | null;
    currentTrack?: TrackVariation | null;
    schedulingTracks: {};
    flags?: LDFlagSet;

    // Forwarded to the object selector
    onObjectSearcherChange: (objectSearch: ObjectSearch) => void;
    onObjectInfo: (
        object: any,
        createCopy: boolean | undefined,
        viewOnly: boolean | undefined,
        title: string
    ) => void;
    onNewObject: (typeId: number, title: string) => void;
    /** Registers a function the object list calls to reload itself. */
    addReloadFunction: (reloadFunction: () => void) => void;
    /** Called with the fluffy item the selection is now on. */
    onFluffyItemChanged?: (newFluffyItem: any) => void;

    // Lock mode, owned by App
    temporaryUnlock?: boolean;
    skipConfirmation?: boolean;

    // Forwarded to the track pane while scheduling activities
    mappedResult: TActivityToReservationMapObj[];
    onCancelActivityScheduling: () => void;
    onCurrentTrackChanged: (trackKey: any) => void;
} & TAppContextProp;

type TSelectionPaneState = {
    selectedItem: TFluffyObjectItem | null;
    selectNextItem: boolean;
    weightObjectList: number;
    weightTypeList: number;
    focusObjectId: number | null;
    displayObjectInfo: boolean;
    templateGroups: IGetTemplateGroupsResponse["parameters"][0];
    showTemplateHelp: boolean;
    lockedItems: any[];
    multiSelectActive: boolean;
    selectedObject: TObject | null;
    isSelectObjectOperation: boolean;
    selectObjectCallback:
        | ((objectIds: number[], activeFilter: TSelectedCategories | null) => void)
        | null;
    selectObjectTypeId: number | null;
    activeFilter: TSelectedCategories | null;
};

class SelectionPane extends React.Component<TSelectionPaneProps, TSelectionPaneState> {
    private _fluffySaveTimeout: any;

    private _objectSelectListRef = React.createRef<HTMLLIElement>();
    private _selectionsOffset: { top: number; left: number } = { top: 0, left: 0 };

    constructor(props) {
        super(props);
        const weights = this.props.appContext.user?.belowbarWeights || [0.5, 0.5];

        this.state = {
            selectedItem: null,
            selectNextItem: false,
            weightObjectList: weights[0],
            weightTypeList: weights[1],
            focusObjectId: null,
            displayObjectInfo: false,
            templateGroups: [],
            showTemplateHelp: false,
            lockedItems: [],
            selectedObject: null,
            isSelectObjectOperation: false,
            selectObjectCallback: null,
            selectObjectTypeId: null,
            multiSelectActive: false,
            activeFilter: null,
        };
    }

    componentDidMount() {
        Mousetrap.bindWithHelp(
            "space",
            () => {
                this.handleToggleObjectInfo();
                return false;
            },
            undefined,
            Language.get("nc_object_list_show_object_information.")
        );
        this.updateSelection();
        API.getPreferences("showTemplateHelp", undefined, undefined, (showTemplateHelp) => {
            this.setState({
                showTemplateHelp: _.isNullish(showTemplateHelp) ? true : Boolean(showTemplateHelp),
            });
        });
        this.registerMacros();
        API.getPreferences("lockedObjects", undefined, undefined, (value) => {
            if (value) {
                const items = JSON.parse(value as any);
                const nameItems = items.map((item) => ({
                    id: item.object.id,
                    type: item.type.id,
                }));
                ObjectNameCache.fetchAndPopulate(nameItems, (names) => {
                    items.forEach((item, index) => {
                        const name = names[index];
                        if (
                            name &&
                            name.fields &&
                            name.fields.length > 0 &&
                            name.fields[0].values &&
                            name.fields[0].values.length > 0
                        ) {
                            // eslint-disable-next-line no-param-reassign
                            item.objectText = name.fields[0].values[0];
                        }
                    });
                    this.setState({
                        lockedItems: items,
                    });
                });
            }
        });
    }

    componentDidUpdate(prevProps: TSelectionPaneProps, prevState) {
        const active = prevProps.activeCalendar;
        const nextActive = this.props.activeCalendar;
        const isActivePrivate = active ? active.privateSelected : false;
        const isNextPrivate = nextActive ? nextActive.privateSelected : false;
        if (isActivePrivate !== isNextPrivate) {
            this.fluffyItemChanged(null, null);
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                selectedItem: null,
            });
            return;
        }

        if (!prevProps.data.isEditMode() && this.props.data.isEditMode()) {
            const firstVirtualObject = _.find(
                this.props.data.fluffy?.objectItems ?? [],
                (item) => item.virtual
            );
            if (firstVirtualObject) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ selectedItem: firstVirtualObject });
            }
        }

        if (!this.props.data.fluffy) {
            const data = this.props.data;
            this.props.data.createFluffy(
                null,
                this.props.activeLayer,
                this.props.activeCalendar
                    ? this.props.activeCalendar.templateKind
                    : TemplateKind.RESERVATION,
                (newSelection) => {
                    this.props.appContext.update(data, newSelection);
                }
            );
            return;
        }

        if (this.props.data.fluffy !== prevProps.data.fluffy) {
            const saveFluffy = this.props.data.fluffy.toJson();
            const FLUFFY_SAVE_TIMEOUT = 5000;
            clearTimeout(this._fluffySaveTimeout);
            this._fluffySaveTimeout = setTimeout(() => {
                API.setPreferences("currentFluffy", [JSON.stringify(saveFluffy)], _.noop);
            }, FLUFFY_SAVE_TIMEOUT);
        }

        if (
            !_.isEqual(
                this.props.data.fluffy ? this.props.data.fluffy.toJson() : null,
                prevProps.data.fluffy ? prevProps.data.fluffy.toJson() : null
            )
        ) {
            this.updateSelection();
        } else if (this.props.data.selectNextItem === true && this.state.selectNextItem === false) {
            this.selectNextItem(
                this.props.data.fluffy,
                this.props.data.selectNextItem,
                this.state.selectedItem
            );
        }

        // Clear selection time limit if a new item is selected
        if (
            this.state.selectedItem !== prevState.selectedItem &&
            this.props.data.hasTimeLimit() &&
            this.props.data.mode !== Selection.EDIT
        ) {
            this.props.appContext.update(this.props.data, this.props.data.removeTimeLimit());
        }
    }

    registerMacros() {
        this.props.appContext.registerMacro("selectionPane", {
            events: [Macros.Event.SELECT_EXTERNAL_TYPE],
            actions: [
                {
                    key: Macros.Action.SELECT_EXTERNAL_TYPE,
                    action: (type, callback = _.noop) => {
                        this.selectType(type.id, callback);
                    },
                },
            ],
        });
        this.props.appContext.registerMacro("selectionPane", {
            events: [Macros.Event.RESERVATION_MADE_OR_MODIFIED],
            actions: [
                {
                    key: Macros.Action.REFRESH,
                    action: (reservationIds = []) => {
                        if (
                            reservationIds.length === 0 &&
                            this.props.data.reservations &&
                            this.props.data.reservations.length > 0
                        ) {
                            this.props.appContext.update(
                                this.props.data,
                                this.props.data.clearReservations()
                            );
                        } else if (
                            this.props.data.isEditMode() &&
                            _.isEqual(reservationIds, this.props.data.reservations)
                        ) {
                            // This is to handle the case where the user changes the reservation by editing its field values in the sidebar.
                            // If that happens, we clear out any field values in the current fluffy to prevent them overwriting the updated values when the reservation is saved.
                            this.clearFieldValues();
                        }
                    },
                },
            ],
        });
        this.props.appContext.registerMacro("selectionPane", {
            events: [Macros.Event.SELECT_EXTERNAL_OBJECT],
            actions: [
                {
                    key: Macros.Action.SELECT_EXTERNAL_OBJECT,
                    action: (typedObjects) => {
                        const makeAdditionOfObjects = (object, remainingObjects, typeId) => {
                            // eslint-disable-next-line no-param-reassign
                            object.typeId = typeId;
                            this.handleAddObject(object, false, () => {
                                if (remainingObjects.length === 0) {
                                    return;
                                }
                                const nextObject = remainingObjects.pop();
                                makeAdditionOfObjects(nextObject, remainingObjects, typeId);
                            });
                        };
                        const allObjects = typedObjects.objects;
                        const firstObject = allObjects.pop();
                        makeAdditionOfObjects(firstObject, allObjects, typedObjects.type.id);
                    },
                },
            ],
        });
        this.props.appContext.registerMacro("selectionPane", {
            events: [Macros.Event.SET_EXTERNAL_SELECTION],
            actions: [
                {
                    key: Macros.Action.SET_EXTERNAL_SELECTION,
                    action: (reservationData, callback = _.noop) => {
                        if (reservationData.reservationMode) {
                            this.getTemplateGroupByExtid(
                                reservationData.reservationMode,
                                (groupId) => {
                                    this.handlePopulateSelection(
                                        reservationData,
                                        groupId,
                                        callback
                                    );
                                }
                            );
                        } else {
                            this.handlePopulateSelection(
                                reservationData,
                                reservationData.templateGroupId,
                                callback
                            );
                        }
                    },
                },
            ],
        });
        this.props.appContext.registerMacro("selectionPane", {
            events: [Macros.Event.REQUEST_OPERATION],
            actions: [
                {
                    key: Macros.Action.REQUEST_OPERATION,
                    action: (options) => {
                        // eslint-disable-next-line no-console
                        if (options.operationType === OPERATION_TYPES.SELECT_OBJECT) {
                            const typeId = options.data.typeId
                                ? options.data.typeId
                                : options.data.type
                                ? options.data.type.id
                                : null;
                            if (typeId) {
                                this.setState(
                                    {
                                        isSelectObjectOperation: true,
                                        selectObjectCallback: options.callback,
                                        selectObjectTypeId: typeId,
                                    },
                                    () => {
                                        if (options.data.reservationData.reservationMode) {
                                            this.setTemplateGroupByExtid(
                                                options.data.reservationData.reservationMode,
                                                () => {
                                                    this.handlePopulateSelection(
                                                        options.data.reservationData
                                                    );
                                                },
                                                true
                                            );
                                        } else {
                                            this.handlePopulateSelection(
                                                options.data.reservationData
                                            );
                                        }
                                    }
                                );
                            } else {
                                Log.error("The object type does not exist in TE Core.");
                                options.callback(null);
                            }
                        }
                    },
                },
            ],
        });
        this.props.appContext.registerMacro("selectionPane", {
            events: [Macros.Event.SET_EXTERNAL_TEMPLATE_GROUP],
            actions: [
                {
                    key: Macros.Action.SET_EXTERNAL_TEMPLATE_GROUP,
                    action: (templateGroupExtid) => {
                        this.setTemplateGroupByExtid(templateGroupExtid);
                    },
                },
            ],
        });
    }

    componentWillUnmount() {
        this.props.appContext.deregisterMacro("selectionPane");
    }

    getTemplateGroupByExtid = (
        templateGroupExtid: string,
        callback: (groupId?: number) => void = _.noop
    ) => {
        let tk = TemplateKind.RESERVATION;
        if (this.props.activeCalendar && this.props.activeCalendar.templateKind) {
            tk = this.props.activeCalendar.templateKind;
        }
        API.getTemplateGroups(tk.number, (result) => {
            const group = _.find(result.parameters[0], (grp) => grp.extid === templateGroupExtid);
            if (group) {
                callback(group.id);
            } else {
                callback();
            }
        });
    };

    setTemplateGroupByExtid = (templateGroupExtid, callback = _.noop, skipUpdates = false) => {
        let tk = TemplateKind.RESERVATION;
        if (this.props.activeCalendar && this.props.activeCalendar.templateKind) {
            tk = this.props.activeCalendar.templateKind;
        }
        API.getTemplateGroups(tk.number, (result) => {
            const group = _.find(result.parameters[0], (grp) => grp.extid === templateGroupExtid);
            if (group && group.id !== this.props.data.fluffy?.templateGroupId) {
                this.handleSelectTemplateGroup(group.id, callback, skipUpdates);
            } else {
                callback();
            }
        });
    };

    handleClearSelection = () => {
        this.clearSelection();
        this.props.appContext.fireEvent(`selectionPane`, Macros.Event.CLEAR_SELECTION);
    };

    handleToggleObjectInfo = (event?: React.MouseEvent | Event | null) => {
        this.setState({ displayObjectInfo: !this.state.displayObjectInfo });
        if (event) {
            event.stopPropagation();
        }
    };

    handleFocusObject = (newObject) => {
        this.setState({
            focusObjectId: newObject ? newObject.id : 0,
        });
    };

    handleFluffyItem = (object, callback: (selection?: any) => void = _.noop, returnSelection?) => {
        // Actually set the item
        this.fluffyItemChanged(
            object,
            this.props.data.fluffy,
            callback,
            this.state.selectNextItem,
            returnSelection
        );
    };

    handleSelectTemplateGroup = (groupId, callback = _.noop, skipUpdates = false) => {
        this.props.data.fluffy?.setTemplateGroup(groupId, (fluffy) => {
            this.updateFluffy(fluffy, false, () => {
                if (!skipUpdates) {
                    if (this.props.activeCalendar) {
                        API.setPreferences(
                            `${this.props.activeCalendar.templateKind.name}lastSelectedTemplateGroup`,
                            [groupId],
                            _.noop
                        );
                    }
                    this.fluffyItemChanged(fluffy.objectItems[0], fluffy, callback);
                } else {
                    callback();
                }
            });
        });
    };

    setTemplateGroups = (allGroups: IGetTemplateGroupsResponse["parameters"][0]) => {
        this.setState({ templateGroups: allGroups });
    };

    handleRemoveObject = (object, nextObject) => {
        let next = nextObject || object;
        this.props.data.fluffy?.removeObject(
            object,
            (newFluffy) => {
                next = newFluffy.getLineForType(next.type.id);
                this.fluffyItemChanged(next, newFluffy, () => {
                    this.updateFluffy(newFluffy);
                });
            },
            false,
            this.props.data.isEditMode()
        );
    };

    handleAddObject = (object, isNotModKey, callback = _.noop) => {
        if (!_.isFunction(callback)) {
            // eslint-disable-next-line no-param-reassign
            callback = _.noop;
        }
        if (this.state.multiSelectActive) {
            return;
        }

        const finish = () => {
            if (
                isNotModKey &&
                this.state.selectedItem?.object &&
                this.state.selectedItem.object.id !== 0
            ) {
                this.props.data.fluffy?.replaceObject(
                    this.state.selectedItem,
                    object,
                    (newFluffy) => this.updateFluffy(newFluffy, false, callback),
                    false,
                    this.props.data.isEditMode()
                );
            } else {
                this.props.data.fluffy?.addObject(
                    object,
                    (newFluffy) => this.updateFluffy(newFluffy, false, callback),
                    false,
                    this.props.data.isEditMode()
                );
            }
        };

        if (isNotModKey && this.props.appContext.user?.jumpToNextType === true) {
            this.setState({ selectNextItem: true, selectedObject: object }, () => {
                finish();
            });
        } else {
            finish();
        }
    };

    handleObjectListClick = (object, filters: TSelectedCategories, isNotModKey) => {
        const activeFilter = filters && Object.keys(filters).length > 0 ? filters : null;
        this.setState({ activeFilter }, () => {
            this.handleAddObject(object, isNotModKey);
        });
    };

    handleMultipleSelected = (objects, selectedIndexes, callback = _.noop) => {
        this.props.data?.fluffy?.addObjects(
            objects,
            (newFluffy) => this.updateFluffy(newFluffy, false, callback),
            false,
            this.props.data.isEditMode()
        );
    };

    handleLockItem = (object) => {
        // Only keep values that are required to add the object to McFluffy
        const lockedItem = _.pick(object, "object", "type", "objectText");
        this.updateLockedItems([lockedItem].concat(this.state.lockedItems));
    };

    handleUnlockItem = (object) => {
        this.updateLockedItems(
            this.state.lockedItems.filter((item) => item.object.id !== object.object.id)
        );
    };

    updateLockedItems = (lockedItems) => {
        this.setState({ lockedItems });
        API.setPreferences("lockedObjects", [JSON.stringify(lockedItems)], _.noop);
    };

    handleUpdate = (data) => {
        if (data.length === 0) {
            return null;
        }

        if (!this.props.activeCalendar) {
            return null;
        }
        this.props.appContext.fireEvent(
            `groupPane`,
            Macros.Event.PROVIDER_UPDATE,
            data,
            this.props.activeCalendar._modelId
        );

        // Set active calendar directly to avoid flickering list. Duplicates handleProviderUpdate in Calendar.jsx
        const providers = this.props.activeCalendar.getProviderMap();
        const headers = this.props.activeCalendar.getHeaderTypeMap();

        if (providers.week && !providers.weekday) {
            const provider = headers.week || headers.weekperiod;
            if (provider) {
                return this.props.appContext.update(
                    provider,
                    provider.immutableSet({ weekdays: data })
                );
            }
        }

        if (providers.weekday && !providers.week) {
            const provider = headers.weekday || headers.weekdayperiod;
            if (provider) {
                return this.props.appContext.update(
                    provider,
                    provider.immutableSet({ weeks: data })
                );
            }
        }

        if (providers.date) {
            const provider = headers.dateperiod;
            if (provider) {
                return this.props.appContext.update(provider, provider.setWeeks(data));
            }
        }
        return null;
    };

    handleHistoryFluffy = (fluffy: McFluffy) => {
        if (fluffy.layer !== this.props.activeLayer) {
            this.updateFluffy(fluffy.setLayer(this.props.activeLayer), false, (updatedFluffy) => {
                const firstItem = updatedFluffy.objectItems[0];
                this.fluffyItemChanged(firstItem, updatedFluffy);
            });
        } else {
            this.updateFluffy(fluffy, false, (updatedFluffy) => {
                const firstItem = updatedFluffy.objectItems[0];
                this.fluffyItemChanged(firstItem, updatedFluffy);
            });
        }
    };

    handlePreviousFluffy = () => {
        if (!this.props.data.hasSnapshots(true)) {
            return;
        }

        this.handleHistoryFluffy(this.props.data.getPreviousSnapshot());
    };

    handleNextFluffy = () => {
        if (!this.props.data.hasSnapshots(false)) {
            return;
        }

        this.handleHistoryFluffy(this.props.data.getNextSnapshot());
    };

    handleToggleTemplateHelp = () => {
        const newValue = !this.state.showTemplateHelp;
        this.setState({ showTemplateHelp: newValue });
        API.setPreferences("showTemplateHelp", [newValue], _.noop);
    };

    getProvider = () => {
        if (!(this.props.activeCalendar instanceof Calendar)) {
            return null;
        }
        const providers = this.props.activeCalendar.getProviderMap();
        const headers = this.props.activeCalendar.getHeaderTypeMap();
        let provider;
        if (providers.week && !providers.weekday) {
            provider = headers.week || headers.weekperiod;
            const INPUT_HEIGHT = 30;
            return (
                <li>
                    <div className="weekdayList">
                        <div className="listTopLine">
                            <h3>{Language.get("cal_list_weekdays")}</h3>
                        </div>
                        <ArrayInput
                            height={this.props.size.height - INPUT_HEIGHT}
                            defaultValue={provider.getWeekdays()}
                            defaultSize={7}
                            limit={7}
                            onUpdate={this.handleUpdate}
                        />
                    </div>
                </li>
            );
        }

        if (providers.weekday && !providers.week) {
            provider = headers.weekday || headers.weekdayperiod;
            return (
                <li>
                    <WeekList
                        outerHeight={this.props.size.height}
                        defaultValue={provider.getWeeks(this.props.appContext.customWeekNames)}
                        onUpdate={this.handleUpdate}
                        customWeekNames={this.props.appContext.customWeekNames}
                        isSchedulingTracks={Object.keys(this.props.schedulingTracks).length > 0}
                    />
                </li>
            );
        }

        if (headers.dateperiod && headers.dateperiod.isWeekPeriod) {
            provider = headers.dateperiod;
            return (
                <li>
                    <WeekList
                        outerHeight={this.props.size.height}
                        defaultValue={provider.getWeeks(this.props.appContext.customWeekNames)}
                        onUpdate={this.handleUpdate}
                        customWeekNames={this.props.appContext.customWeekNames}
                        isSchedulingTracks={Object.keys(this.props.schedulingTracks).length > 0}
                    />
                </li>
            );
        }
        return null;
    };

    getLayerContent = () => {
        const target = {
            bottom: 200,
            left: 20,
            width: 10,
            height: 15,
        };

        return (
            <Popover
                style={{ width: Popover.DEFAULT_WIDTH }}
                target={target}
                bottomArrow={true}
                onClose={this.handleToggleObjectInfo}
            >
                <ObjectInfo
                    user={this.props.user}
                    onClose={this.handleToggleObjectInfo}
                    typeId={this.state.selectedItem ? this.state.selectedItem.type.id : 0}
                    objectId={this.state.focusObjectId}
                />
            </Popover>
        );
    };

    updateSelection = () => {
        // If we do not move to next type and we have the type rather than a object selected
        // keep the selection on the type (i.e. don't select anything else)
        if (
            this.state.selectedItem &&
            !this.state.selectNextItem &&
            (!this.state.selectedItem.object || this.state.selectedItem.object.id === 0)
        ) {
            return;
        }

        this.selectNextItem(
            this.props.data.fluffy,
            this.state.selectNextItem || this.props.data.selectNextItem,
            this.state.selectedItem
        );
    };

    selectType = (typeId, callback) => {
        const items = this.props.data.fluffy?.objectItems || [];
        const selected = _.find(items, (item) => item.type.id === typeId);
        if (selected) {
            this.fluffyItemChanged(selected, this.props.data.fluffy, callback, false);
        } else {
            callback();
        }
    };

    // Used when splitting reservations, and with external input, i.e. data from activity manager
    /**
     * Select the next empty line, or the line the caller asked for when we are
     * running a SELECT_OBJECT operation. No-op when the user has turned off
     * jumping to the next type.
     */
    skipToNextTypeIfNeeded = (fluffy, callback = _.noop) => {
        if (
            this.props.appContext.user?.jumpToNextType === false &&
            !this.state.isSelectObjectOperation
        ) {
            callback();
            return;
        }

        const item = this.state.isSelectObjectOperation
            ? fluffy.objectItems.find((it) => it.type.id === this.state.selectObjectTypeId)
            : fluffy.objectItems.find((it) => it.object.id === 0);

        if (item) {
            // Lots of state setting!
            this.selectFluffyItem(item, fluffy, callback);
        } else {
            callback();
        }
    };

    setTemporarySingleClickReservation = (allowTemporarySingleClickReservation: boolean) => {
        this.props.appContext.update(
            this.props.appContext.user,
            this.props.appContext.user?.immutableSet({ allowTemporarySingleClickReservation })
        );
    };

    // Used when splitting reservations, and with external input, i.e. data from activity manager
    handlePopulateSelection = (reservationData, templateGroupId?: number, cb = _.noop) => {
        const callback = () => {
            this.props.appContext.fireEvent(`selectionPane`, Macros.Event.EXTERNAL_SELECTION_SET);
            cb();
        };
        this.clearSelection(
            false,
            (newSelection) => {
                const objects = newSelection.fluffy.sortByTypes([...reservationData.objects]);
                const duration = reservationData.duration
                    ? reservationData.duration * TC.SECONDS_PER_MINUTE
                    : 0;

                const processResultFluffy = (resultFluffy: McFluffy, success: boolean) => {
                    if (success === false) {
                        return;
                    }

                    let fluffy = resultFluffy;
                    if (reservationData.activityId) {
                        fluffy = fluffy.setActivityInfo({
                            activityId: reservationData.activityId,
                            activitySeriesId: reservationData.activitySeriesId,
                            track: reservationData.activitySeriesId ? reservationData.track : 0,
                        });
                    }
                    if (reservationData.activityInfo) {
                        fluffy = fluffy.setActivityInfo(reservationData.activityInfo);
                    }

                    fluffy.updateLabels((updatedFluffy) => {
                        const populatedFluffy = reservationData.fields
                            ? updatedFluffy.setFields(reservationData.fields, true)
                            : updatedFluffy;

                        this.updateFluffy(populatedFluffy, duration, (newFluffy) => {
                            if (
                                duration > 0 &&
                                !this.props.appContext.user?.allowsSingleClickReservation()
                            ) {
                                this.setTemporarySingleClickReservation(true);
                            }
                            this.skipToNextTypeIfNeeded(newFluffy, callback);
                        });
                    });
                };

                this.addAllObjectsServer(newSelection.fluffy, objects, processResultFluffy);
            },
            true,
            templateGroupId
        );
    };

    selectFluffyItem = (item, fluffy, callback) => {
        this.setState({ selectedItem: item, selectedObject: null, selectNextItem: false }, () => {
            this.selectNextItem(fluffy, false, this.state.selectedItem, callback);
        });
    };

    selectNextItem = (fluffy, selectNextItem, currentSelectedItem, callback = _.noop) => {
        const items = fluffy?.objectItems || [];
        let selected: TFluffyObjectItem | null = null;
        const numReservations = this.props.data.reservations.length;
        const selectedObject = this.state.selectedObject;
        // If currentSelectedItem is a request object, and we are not moving to the next type, keep the selected item
        if (
            !selectNextItem &&
            isRequestObject(currentSelectedItem) &&
            fluffy.objectItems.some(
                // We need to check that currentSelectedItem is still in the fluffy, but straight object equivalence doesn't work since min, max, etc may have changed
                (item) =>
                    item.type.id === currentSelectedItem.type.id &&
                    item.object.id === currentSelectedItem.object.id
            )
        ) {
            selected = currentSelectedItem;
        } else {
            selected = findNextSelectedItem(
                items,
                currentSelectedItem,
                selectedObject,
                selectNextItem,
                numReservations
            );
        }

        this.fluffyItemChanged(
            selected || items[0],
            fluffy,
            () => {
                if (this.props.data.selectNextItem === true) {
                    this.props.appContext.update(
                        this.props.data,
                        this.props.data.setSelectNextItem(false)
                    );
                }
                callback();
            },
            false
        );
    };

    setFluffySize = (newSize) => {
        if (!newSize || newSize === "") {
            return;
        }
        const intSize = parseInt(newSize, 10);
        const newFluffy = this.props.data.fluffy?.setSize(intSize);
        this.props.appContext.update(this.props.data, this.props.data.setFluffy(newFluffy));
    };

    setLockedItems = (
        selection: Selection,
        fluffy,
        lockedItems,
        callback: (selection: Selection) => void | (() => void) = _.noop
    ) => {
        fluffy.setObjects(lockedItems, false, (updatedFluffy) => {
            const lockSelection = selection.setFluffy(updatedFluffy);
            this.props.appContext.update(this.props.data, lockSelection);
            callback(lockSelection);
        });
    };

    clearSelection = (
        preserveLocks = true,
        callback: (val: any) => void = _.noop,
        skipUpdates = false,
        templateGroupId: number | undefined = undefined
    ) => {
        if (!this.props.activeCalendar) {
            callback(this.props.data);
            return;
        }
        if (this.props.appContext.user?.allowTemporarySingleClickReservation && !skipUpdates) {
            this.props.appContext.update(
                this.props.appContext.user,
                this.props.appContext.user?.immutableSet({
                    allowTemporarySingleClickReservation: false,
                })
            );
        }
        API.getPreferences(
            `${this.props.activeCalendar.templateKind.name}lastSelectedTemplateGroup`,
            undefined,
            undefined,
            (foundId) => {
                let groupId: any = templateGroupId;
                if (groupId === undefined) {
                    groupId = foundId || this.props.data.fluffy?.templateGroupId;
                }
                this.props.data.createFluffy(
                    groupId,
                    this.props.activeLayer,
                    this.props.activeCalendar.templateKind,
                    (newSelection) => {
                        const nS = newSelection.immutableSet({ duration: false });
                        if (skipUpdates) {
                            callback(nS);
                            return;
                        }
                        const finish = (finishSelection) => {
                            if (finishSelection.fluffy.objectItems.length > 0) {
                                this.handleFluffyItem(
                                    finishSelection.fluffy.objectItems[0],
                                    callback,
                                    finishSelection
                                );
                            } else {
                                callback(finishSelection);
                            }
                        };
                        if (preserveLocks && this.state.lockedItems.length > 0) {
                            this.setLockedItems(nS, nS.fluffy, [...this.state.lockedItems], finish);
                        } else {
                            this.props.appContext.update(this.props.data, nS);
                            finish(nS);
                        }
                    }
                );
            }
        );
    };

    updateFluffy = (
        newFluffy,
        duration: number | false = false,
        callback: (updatedFluffy: any) => void = _.noop
    ) => {
        if (this.props.data.fluffy && newFluffy) {
            this.props.appContext.update(
                this.props.data,
                this.props.data.setFluffy(newFluffy).setDuration(duration)
            );
        }
        callback(newFluffy);
    };

    fluffyItemChanged = (
        newItem,
        fluffy,
        callback: (selection?: any) => void = _.noop,
        selectNextItem = this.state.selectNextItem,
        returnSelection?
    ) => {
        const finish = (rs) => {
            if (this.props.onFluffyItemChanged) {
                this.props.onFluffyItemChanged(newItem);
            }
            callback(rs);
        };
        if (newItem && fluffy !== null) {
            this.setState({ selectedItem: newItem, selectNextItem }, () => {
                finish(returnSelection);
            });
        } else {
            finish(returnSelection);
        }
    };

    resizeComponents = (resizedComponentIndex, event) => {
        const coords = _.getClientPos(event);
        const weights = getResizeWeights(
            getAvailableWidth(this.props.size.width),
            coords.x - this._selectionsOffset.left,
            resizedComponentIndex,
            this.state
        );

        if (!weights) {
            return;
        }

        this.props.appContext.update(
            this.props.appContext.user,
            this.props.appContext.user?.setBelowbarWeights([
                weights.weightObjectList,
                weights.weightTypeList,
            ])
        );
        this.setState(weights);
    };

    startComponentsResize = (resizedComponentIndex, event) => {
        this._selectionsOffset = _.nodeOffset(
            (this._objectSelectListRef.current?.firstElementChild as HTMLElement | null) ?? null
        );

        event.stopPropagation();
        event.preventDefault();
        const resizeFunc = this.resizeComponents.bind(this, resizedComponentIndex);

        const moveFunc = _.getMoveEvent(event);
        const endFunc = _.getEndEvent(event);

        document.addEventListener(moveFunc, resizeFunc);
        document.addEventListener(endFunc, function removeListeners() {
            document.removeEventListener(moveFunc, resizeFunc);
            document.removeEventListener(endFunc, removeListeners);
        });
    };

    addAllObjectsServer = (fluffy, objects, callback) => {
        if (objects.length === 0) {
            callback(fluffy);
            return;
        }
        const typesWithObjects = {};
        objects.forEach((object) => {
            if (object.fields && !object.id) {
                // Is a filter
                return;
            }
            if (!typesWithObjects[object.typeId]) {
                typesWithObjects[object.typeId] = [];
            }
            // SCHED-940: Filter out duplicates
            const alreadyAdded = typesWithObjects[object.typeId].some(
                (added) => added.id === object.id
            );
            if (!alreadyAdded) {
                typesWithObjects[object.typeId].push(object);
            }
        });
        API.addToMcFluffyServer(fluffy.clearFields().toJson(), typesWithObjects, (result) => {
            const resObject = result[0];
            // The proxy answers either the status that stopped it, or the whole
            // addToMcFluffy response whose own first parameter is that status.
            // An omitted result means 0, which is success.
            const status = resObject.class === "status" ? resObject : resObject.parameters[0];
            if ((status.result ?? 0) < 0) {
                Log.error(status.details ?? "", { code: status.result });
                return callback(fluffy, false);
            }

            const resultFluffy = McFluffy.create(resObject);
            return callback(resultFluffy, true);
        });
    };

    hasSnapshots = (hasPrevious: boolean) => this.props.data.hasSnapshots(hasPrevious);

    setReservation = (reservationIds, callback?) => {
        this.props.data.setReservation(
            _.asArray(reservationIds),
            (newSelection) => {
                const finalSelection = newSelection
                    .setGroups(this.props.data.groups)
                    .setDuration(false);
                if (callback) {
                    callback(finalSelection);
                } else {
                    this.props.appContext.update(this.props.data, finalSelection);
                }
            },
            false,
            false,
            false,
            false,
            this.props.data.clusterKind ?? ClusterKind.NONE
        );
    };

    setSelectionGroup = (entries) => {
        if (!this.props.activeCalendar) {
            return;
        }
        this.props.appContext.update(
            this.props.activeCalendar,
            this.props.activeCalendar.immutableSet({ selectionGroup: entries })
        );
    };

    finishSelectObjectOperation = (objectIds, activeFilter) => {
        this.state.selectObjectCallback?.(objectIds, activeFilter);
        this.setState({
            isSelectObjectOperation: false,
            selectObjectCallback: null,
            selectObjectTypeId: null,
            activeFilter: null,
        });
    };

    getSelectedOperationObjectIds = () =>
        (this.props.data.fluffy?.objectItems ?? [])
            .filter(
                (item) =>
                    item.object &&
                    item.object.id !== 0 &&
                    item.type.id === this.state.selectObjectTypeId
            )
            .map((item) => item.object.id);

    onSelectObjectOperation = () => {
        this.finishSelectObjectOperation(
            this.getSelectedOperationObjectIds(),
            this.state.activeFilter
        );
    };

    onCancelSelectObjectOperation = () => {
        this.finishSelectObjectOperation(null, null);
    };

    onSendToObjectHeader = (objects, doSet) => {
        if (!this.props.activeCalendar) {
            return;
        }
        const headers = this.props.activeCalendar
            .getHeaders()
            .filter((header) => header instanceof ObjectHeader);

        headers.forEach((header) => {
            if (doSet) {
                header.setObjects(
                    objects[0].typeId,
                    objects.map((obj) => obj.id),
                    (updatedHeader) => this.props.appContext.update(header, updatedHeader)
                );
            } else {
                header.addObjects(
                    objects[0].typeId,
                    objects.map((obj) => obj.id),
                    (updatedHeader) => this.props.appContext.update(header, updatedHeader)
                );
            }
        });
    };

    onSendToObjectManager = (objects) => {
        if (this.props.activeCalendar) {
            this.props.appContext.update(
                this.props.activeCalendar,
                this.props.activeCalendar.immutableSet({ objects })
            );
        }
    };

    clearFieldValues = () => {
        const newFluffy = this.props.data.fluffy!.clearFields();
        const newSelection = this.props.data.setFluffy(newFluffy);
        this.props.appContext.update(this.props.data, newSelection);
    };

    toggleMultiSelect = () => {
        this.setState({ multiSelectActive: !this.state.multiSelectActive });
    };

    getSizeDebugInfo = () => {
        if (
            process.env.NODE_ENV !== "development" &&
            this.props.data.fluffy?.hasSize(this.props.appContext.user?.useSecondarySize ?? false)
        ) {
            return (
                <li>
                    <ul>
                        <li className="fluffySize" style={{ paddingTop: "5px" }}>
                            Size:{" "}
                            {this.props.data.fluffy?.getSize(
                                this.props.appContext.user?.useSecondarySize ?? false
                            )}
                        </li>
                    </ul>
                </li>
            );
        } else if (process.env.NODE_ENV === "development") {
            return (
                <li>
                    <ul>
                        <li className="fluffySize" style={{ paddingTop: "5px" }}>
                            Size:{" "}
                            {this.props.data.fluffy?.getSize(
                                this.props.appContext.user?.useSecondarySize ?? false
                            )}
                        </li>
                        <li className="fluffyCapacity" style={{ paddingTop: "5px" }}>
                            Capacity ID: {this.props.data.fluffy!.capacityReservationId}
                        </li>
                    </ul>
                </li>
            );
        }
        return null;
    };

    render() {
        const isNarrow = window.innerWidth < NARROW_WIDTH;

        const classes = ["selections"];
        if (!this.props.isVisible) {
            classes.push("hidden");
        }

        if (!this.props.data.fluffy) {
            return <div className={classes.join(" ")} style={{ height: this.props.size.height }} />;
        }

        const resizeObjectSelect = this.startComponentsResize.bind(this, 0);
        const resizeSelectionList = this.startComponentsResize.bind(this, 1);

        let objectInfo: React.JSX.Element | null = null;
        if (this.state.displayObjectInfo) {
            objectInfo = this.getLayerContent();
        }

        const provider = this.getProvider();

        let templateHelp: React.JSX.Element | null = null;
        if (this.state.showTemplateHelp) {
            const sections = this.state.templateGroups
                .filter((group) => group.id > 0 && group.description)
                .map((group) => ({
                    headline: group.name,
                    text: group.description,
                }));
            if (sections.length > 0) {
                templateHelp = (
                    <li className="templateHelp" style={{ width: HELP_WIDTH }}>
                        <button className="closeButton" onClick={this.handleToggleTemplateHelp} />
                        <TextDisplay
                            headline={Language.get("nc_client_reservation_modes_help_headline")}
                            sections={sections}
                        />
                    </li>
                );
            }
        }

        const hoverButton = (
            <button
                className="deprecated hoverButton objectInfo"
                onClick={this.handleToggleObjectInfo}
                title={Language.get("nc_tooltip_show_detailed_object_information")}
            />
        );

        let fieldList: React.JSX.Element | null = null;
        if (
            this.props.appContext.user?.fieldsInSelection === true &&
            this.props.appContext.user?.fieldsNextToSelection === true
        ) {
            const FIELD_LIST_EXTRA_HEIGHT = 28;
            fieldList = (
                <li className="fieldInformation">
                    <SelectionListFieldEditor
                        disableClear={this.props.data.isEditMode()}
                        height={this.props.size.height + FIELD_LIST_EXTRA_HEIGHT}
                        fields={this.props.data.fluffy.fieldItems.map((fI) => fI.field)}
                        fluffy={this.props.data.fluffy}
                        clearFieldValues={this.clearFieldValues}
                    />
                </li>
            );
        }

        const groupPane =
            this.props.data.belongsToGroup() || this.props.data.isGroupMode ? (
                <GroupPaneWrapper
                    data={this.props.data}
                    activeCalendar={this.props.activeCalendar}
                    unlockedReservations={this.props.unlockedReservations}
                    temporaryUnlock={this.props.temporaryUnlock}
                    skipConfirmation={this.props.skipConfirmation}
                    onEntryInfoOpen={this.props.onEntryInfoOpen}
                    endLockMode={this.props.endLockMode}
                    setReservation={this.setReservation}
                />
            ) : null;

        const { objectListWidth, selectedListWidth } = getListWidths(
            getAvailableWidth(this.props.size.width),
            this.state,
            isNarrow,
            {
                hasProvider: Boolean(provider),
                hasTemplateHelp: Boolean(templateHelp),
                hasGroupPane: Boolean(groupPane),
            }
        );

        const selectObjectButton = this.state.isSelectObjectOperation ? (
            <div style={{ display: "flex" }}>
                <button
                    className="deprecated prefsOperationButton cancel"
                    onClick={this.onCancelSelectObjectOperation}
                >
                    {Language.get("dialog_cancel")}
                </button>
                <button
                    disabled={this.getSelectedOperationObjectIds().length === 0}
                    className="deprecated prefsOperationButton"
                    onClick={this.onSelectObjectOperation}
                >
                    {Language.get("nc_prefs_select_object")}
                </button>
            </div>
        ) : null;

        const hasObjectHeader =
            this.props.activeCalendar && this.props.activeCalendar.hasHeaderType
                ? this.props.activeCalendar.hasHeaderType(ObjectHeader)
                : false;

        const hasObjectManager = this.props.activeCalendar instanceof MemberData;

        let multiButton: React.JSX.Element | null = null;
        if (this.props.appContext.user?.enableMultiSelect) {
            const multiClasses = ["multiSelectButton"];
            if (this.state.multiSelectActive) {
                multiClasses.push("active");
            }
            multiButton = (
                <button
                    className={`deprecated ${multiClasses.join(" ")}`}
                    title={Language.get("nc_multi_selection")}
                    value={String(this.state.multiSelectActive)}
                    onClick={this.toggleMultiSelect}
                />
            );
        }

        let trackPane: React.JSX.Element | null = null;
        if (
            Object.keys(this.props.schedulingTracks).length > 0 &&
            this.props.activeCalendar instanceof Calendar
        ) {
            const headers = this.props.activeCalendar.getHeaderTypeMap();
            const prvd = headers.weekday || headers.weekdayperiod;
            if (prvd) {
                const weeks = prvd
                    .getWeeks(this.props.appContext.customWeekNames)
                    .filter((wk) => wk.selected);
                trackPane = (
                    <li className="trackPane">
                        <TrackPane
                            outerHeight={this.props.size.height}
                            tracks={this.props.schedulingTracks}
                            mappedResult={this.props.mappedResult}
                            currentTrack={this.props.currentTrack!}
                            onCancel={this.props.onCancelActivityScheduling}
                            onUpdate={this.props.onCurrentTrackChanged}
                            numWeeks={weeks.length > 0 ? weeks.length : 1}
                            presentModal={this.props.appContext.presentModal}
                        />
                    </li>
                );
            }
        }

        const sizeDebugInfo = this.getSizeDebugInfo();

        return (
            <div className={classes.join(" ")} style={{ height: this.props.size.height }}>
                <ul>
                    <li className="objectSelectList" ref={this._objectSelectListRef}>
                        <ObjectSelect
                            flags={this.props.flags}
                            data={this.props.data}
                            onObjectSearcherChange={this.props.onObjectSearcherChange}
                            width={objectListWidth}
                            availableHeight={this.props.size.height}
                            selectedType={this.state.selectedItem?.type}
                            subtypes={this.state.selectedItem?.subtypes}
                            selectedFluffyItem={this.state.selectedItem}
                            onClick={this.handleObjectListClick}
                            onMultipleSelected={
                                this.state.multiSelectActive
                                    ? this.handleMultipleSelected
                                    : undefined
                            }
                            onObjectInfo={this.props.onObjectInfo}
                            onSendToObjectHeader={
                                hasObjectHeader ? this.onSendToObjectHeader : null
                            }
                            onSendToObjectManager={
                                hasObjectManager ? this.onSendToObjectManager : null
                            }
                            allowMultiSelection={this.state.multiSelectActive}
                            addReloadFunction={this.props.addReloadFunction}
                            onRowHover={this.handleFocusObject}
                            hoverButton={hoverButton}
                            topLeftButton={multiButton}
                            user={this.props.user}
                        />
                    </li>
                    {isNarrow ? null : (
                        <li
                            className="separator"
                            onTouchStart={resizeObjectSelect}
                            onMouseDown={resizeObjectSelect}
                        >
                            <span />
                            <span />
                        </li>
                    )}
                    <li>
                        <SelectionList
                            flags={this.props.flags}
                            data={this.props.data}
                            currentSelectionGroup={
                                this.props.activeCalendar
                                    ? this.props.activeCalendar.selectionGroup
                                    : []
                            }
                            setSize={this.setFluffySize}
                            clusterKind={this.props.data.clusterKind}
                            setSelectionGroup={this.setSelectionGroup}
                            lockedObjects={this.state.lockedItems}
                            onNewObject={this.props.onNewObject}
                            onObjectInfo={this.props.onObjectInfo}
                            onSelectionClear={this.handleClearSelection}
                            templateKind={
                                this.props.activeCalendar
                                    ? this.props.activeCalendar.templateKind
                                    : TemplateKind.RESERVATION
                            }
                            onHelpToggle={this.handleToggleTemplateHelp}
                            isHelpVisible={this.state.showTemplateHelp}
                            onTemplateSelect={this.handleSelectTemplateGroup}
                            onTemplatesLoaded={this.setTemplateGroups}
                            width={selectedListWidth}
                            height={this.props.size.height}
                            getObjectSearch={this.props.getObjectSearch}
                            selectedFluffyItem={this.state.selectedItem}
                            onChange={this.handleFluffyItem}
                            onRemove={this.handleRemoveObject}
                            onLock={this.handleLockItem}
                            onUnlock={this.handleUnlockItem}
                            onPreviousClick={this.handlePreviousFluffy}
                            onNextClick={this.handleNextFluffy}
                            hasSnapshots={this.hasSnapshots}
                            onObjectAdd={this.handleAddObject}
                            onMultipleSelected={
                                this.state.multiSelectActive
                                    ? this.handleMultipleSelected
                                    : undefined
                            }
                            user={this.props.user}
                            setReservation={this.setReservation}
                            bottomButton={selectObjectButton}
                            fieldsInSelection={this.props.appContext.user?.fieldsInSelection}
                            hasOrderList={this.props.hasOrderList}
                            currentTrack={this.props.currentTrack!}
                        />
                    </li>
                    {isNarrow ? null : (
                        <li
                            className="separator"
                            onTouchStart={resizeSelectionList}
                            onMouseDown={resizeSelectionList}
                        >
                            <span />
                            <span />
                        </li>
                    )}
                    {provider}
                    {objectInfo}
                    {fieldList}
                    {templateHelp}
                    {groupPane}
                    {trackPane}
                    {sizeDebugInfo}
                </ul>
            </div>
        );
    }

    shouldComponentUpdate(nextProps, nextState) {
        // focusObjectId changes on every row hover, so only re-render for it while the
        // object info popover is actually showing it.
        return (
            this.props.data !== nextProps.data ||
            this.state.showTemplateHelp !== nextState.showTemplateHelp ||
            this.state.templateGroups !== nextState.templateGroups ||
            (this.state.displayObjectInfo &&
                this.state.focusObjectId !== nextState.focusObjectId) ||
            this.props.activeCalendar !== nextProps.activeCalendar ||
            this.props.isVisible !== nextProps.isVisible ||
            !_.isEqual(this.props.size, nextProps.size) ||
            this.state.displayObjectInfo !== nextState.displayObjectInfo ||
            this.state.selectNextItem !== nextState.selectNextItem ||
            this.props.data.selectNextItem !== nextProps.data.selectNextItem ||
            this.state.weightObjectList !== nextState.weightObjectList ||
            this.state.weightTypeList !== nextState.weightTypeList ||
            !_.isEqual(this.state.lockedItems, nextState.lockedItems) ||
            !_.isEqual(this.state.selectedItem, nextState.selectedItem) ||
            !_.isEqual(this.props.unlockedReservations, nextProps.unlockedReservations) ||
            !_.isEqual(this.props.data.getGroupIds(), nextProps.data.getGroupIds()) ||
            !_.isEqual(this.props.user, nextProps.user) ||
            this.state.isSelectObjectOperation !== nextState.isSelectObjectOperation ||
            this.state.multiSelectActive !== nextState.multiSelectActive ||
            !_.isEqual(this.props.schedulingTracks, nextProps.schedulingTracks)
        );
    }
}

export default withAppContext(SelectionPane);
